import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  DEFAULT_CORNERS,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  getCraftsmanAdjustment,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectEffectiveDate,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateSFRGLAAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compImprovements = improvements[compID];
  const compType = compTypes[compID];

  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const subjectPropertyIdentification = propertyIdentification.subject;

  if (
    !subjectImprovements ||
    !compImprovements ||
    !subjectPropertyIdentification ||
    isUndefined(depreciationRatio) ||
    !effectiveDate
  ) {
    return undefined;
  }

  const {
    gla,
    corners,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = subjectImprovements;
  const { gla: compGla, propertyType: compPropertyType = 'singleFamilyResidence' } =
    compImprovements;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: gla ? AloftArea.schema.parse(gla).toSqFt().unitAmount : undefined,
      compUnitAmount: compGla ? AloftArea.schema.parse(compGla).toSqFt().unitAmount : undefined,
    });
  }

  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  if (
    isUndefined(zipCode) ||
    isUndefined(gla) ||
    isUndefined(quality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1 ||
    isUndefined(compGla)
  ) {
    return undefined;
  }

  return adjCalculator.getSFRGLAAdj({
    subject: {
      quality,
      sqft: AloftArea.schema.parse(gla),
      zipCode,
      corners: corners ?? DEFAULT_CORNERS,
      propertyType,
    },
    comp: { sqft: AloftArea.schema.parse(compGla), propertyType: compPropertyType },
    depreciationRatio,
  }).adjustment;
};

export const selectSFRGLAAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'mainDwelling'),
  ],
  calculateSFRGLAAdj,
);

export const selectSFRGLAAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'mainDwelling'),
  ],
  (
    adjustmentProps,
    improvements,
    propertyIdentification,
    depreciationRatio,
    effectiveDate,
    compSequence,
    compTypes,
    salesComparisonApproach,
    unitRate,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateSFRGLAAdj(
        adjustmentProps,
        improvements,
        propertyIdentification,
        depreciationRatio,
        effectiveDate,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedSFRGLAAdj = createPriceSelector(
  [selectSFRGLAAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'mainDwelling', adjSettings),
);

export const selectAppliedSFRGLAAdjDisplay = createSelector([selectAppliedSFRGLAAdj], (adj) => {
  if (!adj) return undefined;

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});
